import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, MessageSquare, Package, LogOut, Menu,
  Mail, FileText, Lightbulb, ShoppingBag, Loader2, Trash2, Eye, Check,
  BarChart3, Users, Clock, TrendingUp, Reply,
  CreditCard, Star, Settings, DollarSign, Globe, Share2,
  CheckCircle, XCircle, AlertCircle, FolderOpen, Plus, Upload, X, Edit2, Image,
  BookOpen, Briefcase, Lock, Bell, CalendarDays
} from 'lucide-react';
import { 
  getStats, getSubmissions, updateSubmissionStatus, deleteSubmission, seedProducts,
  getPayments, updatePaymentStatus, deletePayment, getPaymentStats,
  getAdminSettings, updateSettings,
  getAdminPortfolio, createPortfolioProject, updatePortfolioProject, deletePortfolioProject,
  uploadPortfolioImage, deletePortfolioImage
} from '../lib/api';
import { formatDate, getFormTypeLabel, getSubmissionStatusLabel } from '../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/r0wcvy09_file_00000000fee871f598adf429b9fe9c58.png";

const statusColors = {
  new: 'bg-green-100 text-green-700',
  read: 'bg-blue-100 text-blue-700',
  processed: 'bg-gray-100 text-gray-700'
};

const paymentStatusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  verified: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const formTypeIcons = {
  project: FileText,
  demo: Eye,
  idea: Lightbulb,
  product: ShoppingBag,
  contact: Mail
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', category_fr: '', category_en: '', description_fr: '', description_en: '', technologies: '', link: '', order: 0, published: true });
  const [projectSaving, setProjectSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [blogArticles, setBlogArticles] = useState([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title_fr: '', title_en: '', summary_fr: '', summary_en: '', content_fr: '', content_en: '', image_url: '', slug: '', published: true });
  const [articleSaving, setArticleSaving] = useState(false);
  const [jobPostings, setJobPostings] = useState([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState({ title_fr: '', title_en: '', description_fr: '', description_en: '', location: '', job_type: 'full_time', department: '', requirements_fr: '', requirements_en: '', salary_range: '', published: true });
  const [jobSaving, setJobSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [appointments, setAppointments] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'messages') loadSubmissions();
    if (activeTab === 'payments') loadPayments();
    if (activeTab === 'testimonials') loadTestimonials();
    if (activeTab === 'settings') loadSettings();
    if (activeTab === 'portfolio') loadPortfolio();
    if (activeTab === 'blog') loadBlogArticles();
    if (activeTab === 'jobs') loadJobs();
    if (activeTab === 'subscribers') loadSubscribers();
    if (activeTab === 'appointments') loadAppointments();
  }, [activeTab, filterType, filterStatus]);

  const loadData = async () => {
    try {
      const [statsData, submissionsData] = await Promise.all([
        getStats(),
        getSubmissions()
      ]);
      setStats(statsData);
      setSubmissions(submissionsData);
      try { await seedProducts(); } catch (e) { /* Products might already exist */ }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const data = await getSubmissions(filterType || null, filterStatus || null);
      setSubmissions(data);
    } catch (err) { console.error('Failed to load submissions:', err); }
  };

  const loadPayments = async () => {
    try {
      const [paymentsData, statsData] = await Promise.all([
        getPayments(),
        getPaymentStats()
      ]);
      setPayments(paymentsData);
      setPaymentStats(statsData);
    } catch (err) { console.error('Failed to load payments:', err); }
  };

  const loadTestimonials = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTestimonials(data);
    } catch (err) { console.error('Failed to load testimonials:', err); }
  };

  const loadSettings = async () => {
    try {
      const data = await getAdminSettings();
      setSettings(data);
    } catch (err) { console.error('Failed to load settings:', err); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateSubmissionStatus(id, newStatus);
      loadSubmissions();
      loadData();
    } catch (err) { console.error('Failed to update status:', err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await deleteSubmission(id);
      loadSubmissions();
      loadData();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const handlePaymentStatusChange = async (id, newStatus) => {
    try {
      await updatePaymentStatus(id, newStatus);
      loadPayments();
    } catch (err) { console.error('Failed to update payment status:', err); }
  };

  const handlePaymentDelete = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await deletePayment(id);
      loadPayments();
    } catch (err) { console.error('Failed to delete payment:', err); }
  };

  const handleTestimonialApprove = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/testimonials/${id}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      loadTestimonials();
    } catch (err) { console.error('Failed to approve:', err); }
  };

  const handleTestimonialDelete = async (id) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      loadTestimonials();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const loadPortfolio = async () => {
    try {
      const data = await getAdminPortfolio();
      setPortfolioProjects(data);
    } catch (err) { console.error('Failed to load portfolio:', err); }
  };

  const resetProjectForm = () => {
    setProjectForm({ name: '', category_fr: '', category_en: '', description_fr: '', description_en: '', technologies: '', link: '', order: 0, published: true });
    setEditingProject(null);
    setShowProjectForm(false);
  };

  const openEditProject = (project) => {
    setProjectForm({
      name: project.name,
      category_fr: project.category_fr,
      category_en: project.category_en,
      description_fr: project.description_fr,
      description_en: project.description_en,
      technologies: (project.technologies || []).join(', '),
      link: project.link || '',
      order: project.order || 0,
      published: project.published !== false
    });
    setEditingProject(project);
    setShowProjectForm(true);
  };

  const handleProjectSave = async (e) => {
    e.preventDefault();
    setProjectSaving(true);
    try {
      const payload = {
        ...projectForm,
        technologies: projectForm.technologies.split(',').map(t => t.trim()).filter(Boolean),
        images: editingProject ? editingProject.images || [] : []
      };
      if (editingProject) {
        await updatePortfolioProject(editingProject.id, payload);
      } else {
        await createPortfolioProject(payload);
      }
      resetProjectForm();
      loadPortfolio();
    } catch (err) { console.error('Failed to save project:', err); }
    finally { setProjectSaving(false); }
  };

  const handleProjectDelete = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await deletePortfolioProject(id);
      loadPortfolio();
    } catch (err) { console.error('Failed to delete project:', err); }
  };

  const handleImageUpload = async (projectId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(projectId);
    try {
      await uploadPortfolioImage(projectId, file);
      loadPortfolio();
    } catch (err) { console.error('Failed to upload image:', err); }
    finally { setUploadingImage(null); }
  };

  const handleImageDelete = async (projectId, imageIndex) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await deletePortfolioImage(projectId, imageIndex);
      loadPortfolio();
    } catch (err) { console.error('Failed to delete image:', err); }
  };

  // Blog management
  const loadBlogArticles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/blog`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBlogArticles(data);
    } catch (err) { console.error('Failed to load blog:', err); }
  };

  const resetArticleForm = () => {
    setArticleForm({ title_fr: '', title_en: '', summary_fr: '', summary_en: '', content_fr: '', content_en: '', image_url: '', slug: '', published: true });
    setEditingArticle(null);
    setShowArticleForm(false);
  };

  const openEditArticle = (article) => {
    setArticleForm({
      title_fr: article.title_fr || '', title_en: article.title_en || '',
      summary_fr: article.summary_fr || '', summary_en: article.summary_en || '',
      content_fr: article.content_fr || '', content_en: article.content_en || '',
      image_url: article.image_url || '', slug: article.slug || '',
      published: article.published !== false
    });
    setEditingArticle(article);
    setShowArticleForm(true);
  };

  const handleArticleSave = async (e) => {
    e.preventDefault();
    setArticleSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingArticle ? `${API_URL}/api/admin/blog/${editingArticle.id}` : `${API_URL}/api/admin/blog`;
      const method = editingArticle ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(articleForm) });
      resetArticleForm();
      loadBlogArticles();
    } catch (err) { console.error('Failed to save article:', err); }
    finally { setArticleSaving(false); }
  };

  const handleArticleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/blog/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      loadBlogArticles();
    } catch (err) { console.error('Failed to delete article:', err); }
  };

  // Jobs management
  const loadJobs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      setJobPostings(await res.json());
    } catch (err) { console.error('Failed to load jobs:', err); }
  };

  const resetJobForm = () => {
    setJobForm({ title_fr: '', title_en: '', description_fr: '', description_en: '', location: '', job_type: 'full_time', department: '', requirements_fr: '', requirements_en: '', salary_range: '', published: true });
    setEditingJob(null); setShowJobForm(false);
  };

  const openEditJob = (job) => {
    setJobForm({ title_fr: job.title_fr || '', title_en: job.title_en || '', description_fr: job.description_fr || '', description_en: job.description_en || '', location: job.location || '', job_type: job.job_type || 'full_time', department: job.department || '', requirements_fr: job.requirements_fr || '', requirements_en: job.requirements_en || '', salary_range: job.salary_range || '', published: job.published !== false });
    setEditingJob(job); setShowJobForm(true);
  };

  const handleJobSave = async (e) => {
    e.preventDefault(); setJobSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingJob ? `${API_URL}/api/admin/jobs/${editingJob.id}` : `${API_URL}/api/admin/jobs`;
      await fetch(url, { method: editingJob ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(jobForm) });
      resetJobForm(); loadJobs();
    } catch (err) { console.error('Failed to save job:', err); }
    finally { setJobSaving(false); }
  };

  const handleJobDelete = async (id) => {
    if (!window.confirm('Supprimer ce poste ?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/jobs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      loadJobs();
    } catch (err) { console.error('Failed to delete job:', err); }
  };

  const loadSubscribers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/newsletter`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(await res.json());
    } catch (err) { console.error('Failed to load subscribers:', err); }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Supprimer cet abonné ?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/newsletter/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      loadSubscribers();
    } catch (err) { console.error('Failed to delete subscriber:', err); }
  };

  const loadAppointments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/appointments`, { headers: { Authorization: `Bearer ${token}` } });
      setAppointments(await res.json());
    } catch (err) { console.error('Failed to load appointments:', err); }
  };

  const handleUpdateAppointmentStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/appointments/${id}/status?new_status=${newStatus}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      loadAppointments();
    } catch (err) { console.error('Failed to update appointment:', err); }
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/appointments/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      loadAppointments();
    } catch (err) { console.error('Failed to delete appointment:', err); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setPasswordError(''); setPasswordSuccess('');
    if (passwordForm.new_password !== passwordForm.confirm_password) { setPasswordError('Les mots de passe ne correspondent pas'); return; }
    if (passwordForm.new_password.length < 6) { setPasswordError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    setPasswordSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ current_password: passwordForm.current_password, new_password: passwordForm.new_password }) });
      const data = await res.json();
      if (!res.ok) { setPasswordError(data.detail || 'Erreur'); return; }
      setPasswordSuccess('Mot de passe modifié avec succès');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) { setPasswordError('Erreur de connexion'); }
    finally { setPasswordSaving(false); }
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    try {
      await updateSettings(settings);
      setSettingsSuccess(true);
      setTimeout(() => setSettingsSuccess(false), 3000);
    } catch (err) { console.error('Failed to save settings:', err); }
    finally { setSettingsSaving(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const navItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'payments', label: 'Paiements', icon: CreditCard },
    { id: 'testimonials', label: 'Témoignages', icon: Star },
    { id: 'portfolio', label: 'Portfolio', icon: FolderOpen },
    { id: 'blog', label: 'Blog', icon: BookOpen },
    { id: 'jobs', label: 'Emplois', icon: Briefcase },
    { id: 'subscribers', label: 'Newsletter', icon: Bell },
    { id: 'appointments', label: 'Rendez-vous', icon: CalendarDays },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#4A90D9]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA] admin-theme" data-testid="admin-dashboard">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <img src={LOGO_URL} alt="JoSmarTech" className="h-12" />
        </div>
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id ? 'bg-[#4A90D9] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid="admin-logout">
            <LogOut size={20} />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg" data-testid="admin-menu-toggle">
              <Menu size={24} />
            </button>
            <h1 className="text-xl font-semibold text-[#1E3A5F]">
              {navItems.find(i => i.id === activeTab)?.label || 'Dashboard'}
            </h1>
            <a href="/" target="_blank" className="text-[#4A90D9] hover:underline text-sm">Voir le site</a>
          </div>
        </header>

        <main className="p-6">
          {/* ===== DASHBOARD TAB ===== */}
          {activeTab === 'dashboard' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-total">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#4A90D9]/10 flex items-center justify-center"><BarChart3 size={24} className="text-[#4A90D9]" /></div>
                    <span className="text-sm text-gray-500">Total</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1E3A5F]">{stats?.total_messages || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Messages reçus</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-new">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center"><Clock size={24} className="text-green-600" /></div>
                    <span className="text-sm text-gray-500">Nouveaux</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1E3A5F]">{stats?.new_messages || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Non traités</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-projects">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center"><FileText size={24} className="text-purple-600" /></div>
                    <span className="text-sm text-gray-500">Projets</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1E3A5F]">{stats?.projects || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Demandes</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-demos">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center"><Eye size={24} className="text-orange-600" /></div>
                    <span className="text-sm text-gray-500">Démos</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1E3A5F]">{stats?.demos || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">Demandées</p>
                </div>
              </div>

              {/* Recent Messages */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b"><h2 className="text-lg font-semibold text-[#1E3A5F]">Messages récents</h2></div>
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead><tr><th>Type</th><th>Nom</th><th>Email</th><th>Date</th><th>Statut</th></tr></thead>
                    <tbody>
                      {submissions.slice(0, 5).map((sub) => {
                        const TypeIcon = formTypeIcons[sub.form_type] || Mail;
                        return (
                          <tr key={sub.id} className="cursor-pointer hover:bg-blue-50" onClick={() => setActiveTab('messages')}>
                            <td><div className="flex items-center gap-2"><TypeIcon size={16} className="text-gray-400" />{getFormTypeLabel(sub.form_type)}</div></td>
                            <td className="font-medium text-gray-800">{sub.name}</td>
                            <td className="text-gray-600">{sub.email}</td>
                            <td className="text-gray-600">{formatDate(sub.created_at)}</td>
                            <td><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[sub.status]}`}>{getSubmissionStatusLabel(sub.status)}</span></td>
                          </tr>
                        );
                      })}
                      {submissions.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-gray-500">Aucun message</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* ===== MESSAGES TAB ===== */}
          {activeTab === 'messages' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-wrap gap-4">
                <div className="flex flex-col">
                  <label className="text-xs text-gray-500 mb-1 font-medium">Type</label>
                  <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="filter-type">
                    <option value="">Tous les types</option>
                    <option value="project">Projets</option>
                    <option value="demo">Démos</option>
                    <option value="idea">Idées</option>
                    <option value="product">Produits</option>
                    <option value="contact">Contact</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-gray-500 mb-1 font-medium">Statut</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="filter-status">
                    <option value="">Tous</option>
                    <option value="new">Nouveaux</option>
                    <option value="read">Lus</option>
                    <option value="processed">Traités</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                {submissions.map((sub) => {
                  const TypeIcon = formTypeIcons[sub.form_type] || Mail;
                  return (
                    <motion.div key={sub.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`message-${sub.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#4A90D9]/10 flex items-center justify-center"><TypeIcon size={20} className="text-[#4A90D9]" /></div>
                          <div><h3 className="font-semibold text-[#1E3A5F]">{sub.name}</h3><p className="text-sm text-gray-500">{sub.email}</p></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[sub.status]}`}>{getSubmissionStatusLabel(sub.status)}</span>
                          <span className="text-xs text-gray-400">{formatDate(sub.created_at)}</span>
                        </div>
                      </div>
                      <div className="mb-4">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {getFormTypeLabel(sub.form_type)}{sub.project_type && ` - ${sub.project_type}`}{sub.product && ` - ${sub.product}`}{sub.subject && ` - ${sub.subject}`}{sub.budget && ` - Budget: ${sub.budget}`}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap mb-4">{sub.message}</p>
                      <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
                        <a href={`mailto:${sub.email}?subject=Re: ${getFormTypeLabel(sub.form_type)} - JoSmarTech`} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#4A90D9] text-white rounded-lg hover:bg-[#5BA0E9] transition-colors" data-testid={`reply-${sub.id}`}><Reply size={14} />Répondre</a>
                        {sub.status === 'new' && <button onClick={() => handleStatusChange(sub.id, 'read')} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-testid={`mark-read-${sub.id}`}><Eye size={14} />Marquer lu</button>}
                        {sub.status !== 'processed' && <button onClick={() => handleStatusChange(sub.id, 'processed')} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors" data-testid={`mark-processed-${sub.id}`}><Check size={14} />Traité</button>}
                        <button onClick={() => handleDelete(sub.id)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors ml-auto" data-testid={`delete-${sub.id}`}><Trash2 size={14} />Supprimer</button>
                      </div>
                    </motion.div>
                  );
                })}
                {submissions.length === 0 && <div className="bg-white rounded-xl p-12 shadow-sm text-center"><MessageSquare size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucun message</p></div>}
              </div>
            </motion.div>
          )}

          {/* ===== PAYMENTS TAB ===== */}
          {activeTab === 'payments' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {/* Payment Stats */}
              {paymentStats && (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="payment-stat-total">
                    <div className="flex items-center gap-3"><DollarSign size={20} className="text-[#4A90D9]" /><div><p className="text-2xl font-bold text-[#1E3A5F]">{paymentStats.total}</p><p className="text-xs text-gray-500">Total</p></div></div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="payment-stat-pending">
                    <div className="flex items-center gap-3"><AlertCircle size={20} className="text-yellow-500" /><div><p className="text-2xl font-bold text-[#1E3A5F]">{paymentStats.pending}</p><p className="text-xs text-gray-500">En attente</p></div></div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="payment-stat-verified">
                    <div className="flex items-center gap-3"><CheckCircle size={20} className="text-blue-500" /><div><p className="text-2xl font-bold text-[#1E3A5F]">{paymentStats.verified}</p><p className="text-xs text-gray-500">Vérifiés</p></div></div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="payment-stat-confirmed">
                    <div className="flex items-center gap-3"><Check size={20} className="text-green-500" /><div><p className="text-2xl font-bold text-[#1E3A5F]">{paymentStats.confirmed}</p><p className="text-xs text-gray-500">Confirmés</p></div></div>
                  </div>
                </div>
              )}

              {/* Payments List */}
              <div className="space-y-4">
                {payments.map((payment) => (
                  <motion.div key={payment.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`payment-${payment.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#4A90D9]/10 flex items-center justify-center"><CreditCard size={20} className="text-[#4A90D9]" /></div>
                        <div>
                          <h3 className="font-semibold text-[#1E3A5F]">{payment.name}</h3>
                          <p className="text-sm text-gray-500">{payment.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${paymentStatusColors[payment.status] || 'bg-gray-100 text-gray-700'}`}>
                          {payment.status === 'pending' ? 'En attente' : payment.status === 'verified' ? 'Vérifié' : payment.status === 'confirmed' ? 'Confirmé' : payment.status === 'rejected' ? 'Rejeté' : payment.status}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(payment.created_at)}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 text-sm">
                      <div><span className="text-gray-500 block">Montant</span><span className="font-semibold text-[#1E3A5F]">{payment.amount} {payment.currency}</span></div>
                      <div><span className="text-gray-500 block">Méthode</span><span className="font-medium text-gray-700">{payment.payment_method}</span></div>
                      <div><span className="text-gray-500 block">Type</span><span className="font-medium text-gray-700">{payment.payment_type}</span></div>
                      {payment.app_name && <div><span className="text-gray-500 block">Application</span><span className="font-medium text-gray-700">{payment.app_name}</span></div>}
                    </div>

                    {payment.message && <p className="text-gray-600 text-sm mb-4">{payment.message}</p>}

                    {payment.proof_image && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">Preuve de paiement :</p>
                        <img src={payment.proof_image} alt="Preuve" className="max-h-48 rounded-lg border border-gray-200" />
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
                      {payment.status === 'pending' && (
                        <>
                          <button onClick={() => handlePaymentStatusChange(payment.id, 'verified')} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-testid={`verify-payment-${payment.id}`}><CheckCircle size={14} />Vérifier</button>
                          <button onClick={() => handlePaymentStatusChange(payment.id, 'rejected')} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" data-testid={`reject-payment-${payment.id}`}><XCircle size={14} />Rejeter</button>
                        </>
                      )}
                      {payment.status === 'verified' && (
                        <button onClick={() => handlePaymentStatusChange(payment.id, 'confirmed')} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors" data-testid={`confirm-payment-${payment.id}`}><Check size={14} />Confirmer</button>
                      )}
                      <button onClick={() => handlePaymentDelete(payment.id)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors ml-auto" data-testid={`delete-payment-${payment.id}`}><Trash2 size={14} />Supprimer</button>
                    </div>
                  </motion.div>
                ))}
                {payments.length === 0 && <div className="bg-white rounded-xl p-12 shadow-sm text-center"><CreditCard size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucun paiement</p></div>}
              </div>
            </motion.div>
          )}

          {/* ===== TESTIMONIALS TAB ===== */}
          {activeTab === 'testimonials' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <motion.div key={testimonial.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`testimonial-${testimonial.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#4A90D9] flex items-center justify-center text-white font-semibold">{testimonial.name.charAt(0).toUpperCase()}</div>
                        <div>
                          <h3 className="font-semibold text-[#1E3A5F]">{testimonial.name}</h3>
                          <p className="text-sm text-gray-500">{testimonial.role}{testimonial.role && testimonial.company && ' - '}{testimonial.company}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${testimonial.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {testimonial.approved ? 'Approuvé' : 'En attente'}
                        </span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => <Star key={s} size={14} className={s <= testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />)}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">"{testimonial.message}"</p>
                    <div className="flex items-center gap-2 pt-4 border-t">
                      {!testimonial.approved && (
                        <button onClick={() => handleTestimonialApprove(testimonial.id)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors" data-testid={`approve-testimonial-${testimonial.id}`}><Check size={14} />Approuver</button>
                      )}
                      <button onClick={() => handleTestimonialDelete(testimonial.id)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors ml-auto" data-testid={`delete-testimonial-${testimonial.id}`}><Trash2 size={14} />Supprimer</button>
                    </div>
                  </motion.div>
                ))}
                {testimonials.length === 0 && <div className="bg-white rounded-xl p-12 shadow-sm text-center"><Star size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucun témoignage</p></div>}
              </div>
            </motion.div>
          )}

          {/* ===== PORTFOLIO TAB ===== */}
          {activeTab === 'portfolio' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {/* Add/Edit Form */}
              {showProjectForm ? (
                <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-[#1E3A5F]">{editingProject ? 'Modifier le projet' : 'Nouveau projet'}</h2>
                    <button onClick={resetProjectForm} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X size={20} /></button>
                  </div>
                  <form onSubmit={handleProjectSave} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom du projet *</label>
                        <input type="text" required value={projectForm.name} onChange={(e) => setProjectForm({...projectForm, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Ex: JoSmart Chat" data-testid="portfolio-input-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Lien (URL)</label>
                        <input type="url" value={projectForm.link} onChange={(e) => setProjectForm({...projectForm, link: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="https://play.google.com/..." data-testid="portfolio-input-link" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie (FR) *</label>
                        <input type="text" required value={projectForm.category_fr} onChange={(e) => setProjectForm({...projectForm, category_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Ex: Application Mobile" data-testid="portfolio-input-cat-fr" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie (EN) *</label>
                        <input type="text" required value={projectForm.category_en} onChange={(e) => setProjectForm({...projectForm, category_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Ex: Mobile Application" data-testid="portfolio-input-cat-en" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (FR) *</label>
                      <textarea required rows={3} value={projectForm.description_fr} onChange={(e) => setProjectForm({...projectForm, description_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-none" placeholder="Description du projet en français..." data-testid="portfolio-input-desc-fr" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (EN) *</label>
                      <textarea required rows={3} value={projectForm.description_en} onChange={(e) => setProjectForm({...projectForm, description_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-none" placeholder="Project description in English..." data-testid="portfolio-input-desc-en" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Technologies (séparées par virgule)</label>
                        <input type="text" value={projectForm.technologies} onChange={(e) => setProjectForm({...projectForm, technologies: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Android, Firebase, API REST" data-testid="portfolio-input-tech" />
                      </div>
                      <div className="flex items-end gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                          <input type="number" value={projectForm.order} onChange={(e) => setProjectForm({...projectForm, order: parseInt(e.target.value) || 0})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="portfolio-input-order" />
                        </div>
                        <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
                          <input type="checkbox" checked={projectForm.published} onChange={(e) => setProjectForm({...projectForm, published: e.target.checked})} className="w-4 h-4 accent-[#4A90D9]" />
                          <span className="text-sm text-gray-700">Publié</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="submit" disabled={projectSaving} className="px-6 py-2.5 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="portfolio-save-btn">
                        {projectSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {projectSaving ? 'Enregistrement...' : (editingProject ? 'Mettre à jour' : 'Créer le projet')}
                      </button>
                      <button type="button" onClick={resetProjectForm} className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors">Annuler</button>
                    </div>
                  </form>
                </div>
              ) : (
                <button onClick={() => setShowProjectForm(true)} className="mb-6 px-6 py-3 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors flex items-center gap-2" data-testid="portfolio-add-btn">
                  <Plus size={18} />
                  Ajouter un projet
                </button>
              )}

              {/* Projects List */}
              <div className="space-y-6">
                {portfolioProjects.map((project) => (
                  <div key={project.id} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`portfolio-project-${project.id}`}>
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-bold text-[#1E3A5F]">{project.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${project.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {project.published ? 'Publié' : 'Brouillon'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{project.category_fr} / {project.category_en}</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => openEditProject(project)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`portfolio-edit-${project.id}`}><Edit2 size={16} /></button>
                          <button onClick={() => handleProjectDelete(project.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid={`portfolio-delete-${project.id}`}><Trash2 size={16} /></button>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-4">{project.description_fr}</p>

                      {/* Technologies */}
                      {project.technologies && project.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.technologies.map((tech) => (
                            <span key={tech} className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#4A90D9]/10 text-[#4A90D9]">{tech}</span>
                          ))}
                        </div>
                      )}

                      {project.link && <p className="text-sm text-gray-500 mb-4">Lien : <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-[#4A90D9] hover:underline">{project.link}</a></p>}

                      {/* Images */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Image size={16} />Photos ({(project.images || []).length})</h4>
                          <label className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#4A90D9]/10 text-[#4A90D9] rounded-lg cursor-pointer hover:bg-[#4A90D9]/20 transition-colors font-medium">
                            {uploadingImage === project.id ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            Ajouter une photo
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(project.id, e)} data-testid={`portfolio-upload-${project.id}`} />
                          </label>
                        </div>
                        {project.images && project.images.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {project.images.map((img, idx) => (
                              <div key={idx} className="relative group rounded-lg overflow-hidden border border-gray-200">
                                <img src={img} alt={`${project.name} ${idx + 1}`} className="w-full h-28 object-cover" />
                                <button
                                  onClick={() => handleImageDelete(project.id, idx)}
                                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  data-testid={`portfolio-delete-img-${project.id}-${idx}`}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm">Aucune photo. Cliquez sur "Ajouter une photo" pour commencer.</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {portfolioProjects.length === 0 && (
                  <div className="bg-white rounded-xl p-12 shadow-sm text-center">
                    <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun projet dans le portfolio</p>
                    <p className="text-gray-400 text-sm mt-1">Cliquez sur "Ajouter un projet" pour commencer</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ===== BLOG TAB ===== */}
          {activeTab === 'blog' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {showArticleForm ? (
                <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-[#1E3A5F]">{editingArticle ? 'Modifier l\'article' : 'Nouvel article'}</h2>
                    <button onClick={resetArticleForm} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X size={20} /></button>
                  </div>
                  <form onSubmit={handleArticleSave} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Titre (FR) *</label>
                        <input type="text" required value={articleForm.title_fr} onChange={(e) => setArticleForm({...articleForm, title_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="blog-input-title-fr" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Titre (EN) *</label>
                        <input type="text" required value={articleForm.title_en} onChange={(e) => setArticleForm({...articleForm, title_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="blog-input-title-en" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Résumé (FR) *</label>
                        <textarea required rows={2} value={articleForm.summary_fr} onChange={(e) => setArticleForm({...articleForm, summary_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-none" data-testid="blog-input-summary-fr" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Résumé (EN) *</label>
                        <textarea required rows={2} value={articleForm.summary_en} onChange={(e) => setArticleForm({...articleForm, summary_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-none" data-testid="blog-input-summary-en" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (FR) * <span className="text-gray-400 font-normal">— Supporte le Markdown</span></label>
                      <textarea required rows={8} value={articleForm.content_fr} onChange={(e) => setArticleForm({...articleForm, content_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-y font-mono text-sm" data-testid="blog-input-content-fr" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (EN) * <span className="text-gray-400 font-normal">— Supports Markdown</span></label>
                      <textarea required rows={8} value={articleForm.content_en} onChange={(e) => setArticleForm({...articleForm, content_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-y font-mono text-sm" data-testid="blog-input-content-en" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">URL de l'image</label>
                        <input type="url" value={articleForm.image_url} onChange={(e) => setArticleForm({...articleForm, image_url: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="https://..." data-testid="blog-input-image" />
                      </div>
                      <div className="flex items-end gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
                          <input type="text" value={articleForm.slug} onChange={(e) => setArticleForm({...articleForm, slug: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="mon-article" data-testid="blog-input-slug" />
                        </div>
                        <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
                          <input type="checkbox" checked={articleForm.published} onChange={(e) => setArticleForm({...articleForm, published: e.target.checked})} className="w-4 h-4 accent-[#4A90D9]" />
                          <span className="text-sm text-gray-700">Publié</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="submit" disabled={articleSaving} className="px-6 py-2.5 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="blog-save-btn">
                        {articleSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {articleSaving ? 'Enregistrement...' : (editingArticle ? 'Mettre à jour' : 'Publier')}
                      </button>
                      <button type="button" onClick={resetArticleForm} className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors">Annuler</button>
                    </div>
                  </form>
                </div>
              ) : (
                <button onClick={() => setShowArticleForm(true)} className="mb-6 px-6 py-3 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors flex items-center gap-2" data-testid="blog-add-btn">
                  <Plus size={18} />
                  Nouvel article
                </button>
              )}

              <div className="space-y-4">
                {blogArticles.map((article) => (
                  <div key={article.id} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`blog-article-${article.id}`}>
                    <div className="flex items-start gap-4 p-6">
                      {article.image_url && <img src={article.image_url} alt="" className="w-24 h-16 object-cover rounded-lg flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-[#1E3A5F] truncate">{article.title_fr}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${article.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {article.published ? 'Publié' : 'Brouillon'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{article.summary_fr}</p>
                        <p className="text-xs text-gray-400 mt-1">/{article.slug || article.id}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <a href={`/blog/${article.slug || article.id}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-[#4A90D9] hover:bg-blue-50 rounded-lg transition-colors"><Eye size={16} /></a>
                        <button onClick={() => openEditArticle(article)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`blog-edit-${article.id}`}><Edit2 size={16} /></button>
                        <button onClick={() => handleArticleDelete(article.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid={`blog-delete-${article.id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {blogArticles.length === 0 && (
                  <div className="bg-white rounded-xl p-12 shadow-sm text-center">
                    <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun article</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ===== JOBS TAB ===== */}
          {activeTab === 'jobs' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {showJobForm ? (
                <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-[#1E3A5F]">{editingJob ? 'Modifier le poste' : 'Nouveau poste'}</h2>
                    <button onClick={resetJobForm} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X size={20} /></button>
                  </div>
                  <form onSubmit={handleJobSave} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Titre (FR) *</label><input type="text" required value={jobForm.title_fr} onChange={(e) => setJobForm({...jobForm, title_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="job-input-title-fr" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Titre (EN) *</label><input type="text" required value={jobForm.title_en} onChange={(e) => setJobForm({...jobForm, title_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="job-input-title-en" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Département</label><input type="text" value={jobForm.department} onChange={(e) => setJobForm({...jobForm, department: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Ex: Développement" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label><input type="text" value={jobForm.location} onChange={(e) => setJobForm({...jobForm, location: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Ex: Remote / Kinshasa" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <select value={jobForm.job_type} onChange={(e) => setJobForm({...jobForm, job_type: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800">
                          <option value="full_time">Temps plein</option><option value="part_time">Temps partiel</option><option value="contract">Contrat</option><option value="internship">Stage</option><option value="remote">Télétravail</option>
                        </select>
                      </div>
                    </div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Salaire</label><input type="text" value={jobForm.salary_range} onChange={(e) => setJobForm({...jobForm, salary_range: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" placeholder="Ex: 500-1000 USD/mois" /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Description (FR) *</label><textarea required rows={4} value={jobForm.description_fr} onChange={(e) => setJobForm({...jobForm, description_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-y" data-testid="job-input-desc-fr" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Description (EN) *</label><textarea required rows={4} value={jobForm.description_en} onChange={(e) => setJobForm({...jobForm, description_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-y" data-testid="job-input-desc-en" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Exigences (FR)</label><textarea rows={3} value={jobForm.requirements_fr} onChange={(e) => setJobForm({...jobForm, requirements_fr: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-y" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Exigences (EN)</label><textarea rows={3} value={jobForm.requirements_en} onChange={(e) => setJobForm({...jobForm, requirements_en: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800 resize-y" /></div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={jobForm.published} onChange={(e) => setJobForm({...jobForm, published: e.target.checked})} className="w-4 h-4 accent-[#4A90D9]" /><span className="text-sm text-gray-700">Publié</span></label>
                    <div className="flex gap-3 pt-2">
                      <button type="submit" disabled={jobSaving} className="px-6 py-2.5 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="job-save-btn">{jobSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}{jobSaving ? 'Enregistrement...' : (editingJob ? 'Mettre à jour' : 'Publier')}</button>
                      <button type="button" onClick={resetJobForm} className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors">Annuler</button>
                    </div>
                  </form>
                </div>
              ) : (
                <button onClick={() => setShowJobForm(true)} className="mb-6 px-6 py-3 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors flex items-center gap-2" data-testid="job-add-btn"><Plus size={18} />Nouveau poste</button>
              )}
              <div className="space-y-4">
                {jobPostings.map((job) => (
                  <div key={job.id} className="bg-white rounded-xl shadow-sm p-6" data-testid={`admin-job-${job.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1"><h3 className="font-bold text-[#1E3A5F]">{job.title_fr}</h3><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${job.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{job.published ? 'Publié' : 'Brouillon'}</span></div>
                        <p className="text-sm text-gray-500">{job.department}{job.location ? ` - ${job.location}` : ''}{job.job_type ? ` - ${job.job_type}` : ''}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openEditJob(job)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`job-edit-${job.id}`}><Edit2 size={16} /></button>
                        <button onClick={() => handleJobDelete(job.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid={`job-delete-${job.id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {jobPostings.length === 0 && <div className="bg-white rounded-xl p-12 shadow-sm text-center"><Briefcase size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucun poste</p></div>}
              </div>
            </motion.div>
          )}

          {/* ===== SUBSCRIBERS TAB ===== */}
          {activeTab === 'subscribers' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#4A90D9]/10 flex items-center justify-center"><Bell size={20} className="text-[#4A90D9]" /></div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#1E3A5F]">Abonnés Newsletter</h2>
                      <p className="text-sm text-gray-500">{subscribers.length} abonné{subscribers.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {subscribers.map((sub) => (
                  <div key={sub.id} className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between" data-testid={`subscriber-${sub.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#4A90D9] flex items-center justify-center text-white font-semibold text-sm">{sub.email.charAt(0).toUpperCase()}</div>
                      <div>
                        <p className="font-medium text-[#1E3A5F]">{sub.email}</p>
                        <p className="text-xs text-gray-400">{sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteSubscriber(sub.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-subscriber-${sub.id}`}><Trash2 size={16} /></button>
                  </div>
                ))}
                {subscribers.length === 0 && (
                  <div className="bg-white rounded-xl p-12 shadow-sm text-center">
                    <Bell size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun abonné pour le moment</p>
                    <p className="text-gray-400 text-sm mt-1">Les visiteurs peuvent s'abonner depuis la page Blog</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ===== APPOINTMENTS TAB ===== */}
          {activeTab === 'appointments' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#4A90D9]/10 flex items-center justify-center"><CalendarDays size={20} className="text-[#4A90D9]" /></div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#1E3A5F]">Rendez-vous</h2>
                    <p className="text-sm text-gray-500">{appointments.length} demande{appointments.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {appointments.map((apt) => (
                  <div key={apt.id} className="bg-white rounded-xl p-5 shadow-sm" data-testid={`appointment-${apt.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-[#1E3A5F]">{apt.name}</p>
                        <p className="text-sm text-gray-500">{apt.email} • {apt.phone}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        apt.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        apt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>{apt.status === 'pending' ? 'En attente' : apt.status === 'confirmed' ? 'Confirmé' : apt.status === 'rejected' ? 'Refusé' : 'Terminé'}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1"><CalendarDays size={14} /> {apt.date}</span>
                      <span className="flex items-center gap-1"><Clock size={14} /> {apt.time}</span>
                      <span className="flex items-center gap-1"><Briefcase size={14} /> {apt.service}</span>
                    </div>
                    {apt.message && <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3 mb-3">{apt.message}</p>}
                    <div className="flex gap-2">
                      {apt.status === 'pending' && (
                        <>
                          <button onClick={() => handleUpdateAppointmentStatus(apt.id, 'confirmed')} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors flex items-center gap-1"><CheckCircle size={14} /> Confirmer</button>
                          <button onClick={() => handleUpdateAppointmentStatus(apt.id, 'rejected')} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors flex items-center gap-1"><XCircle size={14} /> Refuser</button>
                        </>
                      )}
                      {apt.status === 'confirmed' && (
                        <button onClick={() => handleUpdateAppointmentStatus(apt.id, 'completed')} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors flex items-center gap-1"><CheckCircle size={14} /> Marquer terminé</button>
                      )}
                      <button onClick={() => handleDeleteAppointment(apt.id)} className="px-3 py-1.5 bg-gray-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 transition-colors flex items-center gap-1"><Trash2 size={14} /> Supprimer</button>
                    </div>
                  </div>
                ))}
                {appointments.length === 0 && (
                  <div className="bg-white rounded-xl p-12 shadow-sm text-center">
                    <CalendarDays size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun rendez-vous pour le moment</p>
                    <p className="text-gray-400 text-sm mt-1">Les demandes de rendez-vous apparaîtront ici</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ===== SETTINGS TAB ===== */}
          {activeTab === 'settings' && settings && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <form onSubmit={handleSettingsSave} className="space-y-8">
                {/* Social Links */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-6">
                    <Share2 size={20} className="text-[#4A90D9]" />
                    <h2 className="text-lg font-semibold text-[#1E3A5F]">Réseaux sociaux</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { key: 'facebook_url', label: 'Facebook', placeholder: 'https://facebook.com/...' },
                      { key: 'tiktok_url', label: 'TikTok', placeholder: 'https://tiktok.com/@...' },
                      { key: 'instagram_url', label: 'Instagram', placeholder: 'https://instagram.com/...' },
                      { key: 'snapchat_url', label: 'Snapchat', placeholder: 'https://snapchat.com/add/...' },
                      { key: 'twitter_url', label: 'Twitter / X', placeholder: 'https://x.com/...' },
                      { key: 'linkedin_url', label: 'LinkedIn', placeholder: 'https://linkedin.com/...' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                        <input
                          type="url"
                          value={settings[key] || ''}
                          onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                          placeholder={placeholder}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800"
                          data-testid={`settings-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-6">
                    <CreditCard size={20} className="text-[#4A90D9]" />
                    <h2 className="text-lg font-semibold text-[#1E3A5F]">Méthodes de paiement</h2>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">Ces informations seront affichées sur la page de paiement pour vos clients.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { key: 'paypal_email', label: 'PayPal (email)', placeholder: 'votre@email.com' },
                      { key: 'stripe_info', label: 'Stripe / Card Info', placeholder: 'Lien ou info carte' },
                      { key: 'mpesa_number', label: 'M-Pesa (numéro)', placeholder: '+254...' },
                      { key: 'orange_money_number', label: 'Orange Money (numéro)', placeholder: '+237...' },
                      { key: 'airtel_money_number', label: 'Airtel Money (numéro)', placeholder: '+243...' },
                      { key: 'etransfer_email', label: 'E-Transfer (email)', placeholder: 'votre@email.com' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                        <input
                          type="text"
                          value={settings[key] || ''}
                          onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                          placeholder={placeholder}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800"
                          data-testid={`settings-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex items-center gap-4">
                  <button
                    type="submit"
                    disabled={settingsSaving}
                    className="px-8 py-3 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid="settings-save-btn"
                  >
                    {settingsSaving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                    {settingsSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                  {settingsSuccess && <span className="text-green-600 text-sm font-medium flex items-center gap-1"><CheckCircle size={16} />Paramètres sauvegardés</span>}
                </div>
              </form>

              {/* Change Password */}
              <div className="bg-white rounded-xl p-6 shadow-sm mt-8">
                <div className="flex items-center gap-2 mb-6">
                  <Lock size={20} className="text-[#4A90D9]" />
                  <h2 className="text-lg font-semibold text-[#1E3A5F]">Changer le mot de passe</h2>
                </div>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel *</label>
                    <input type="password" required value={passwordForm.current_password} onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="settings-current-password" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe *</label>
                    <input type="password" required value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="settings-new-password" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le nouveau mot de passe *</label>
                    <input type="password" required value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] outline-none bg-white text-gray-800" data-testid="settings-confirm-password" />
                  </div>
                  {passwordError && <p className="text-red-600 text-sm">{passwordError}</p>}
                  {passwordSuccess && <p className="text-green-600 text-sm flex items-center gap-1"><CheckCircle size={14} />{passwordSuccess}</p>}
                  <button type="submit" disabled={passwordSaving} className="px-6 py-2.5 bg-[#1E3A5F] text-white rounded-lg font-semibold hover:bg-[#2a4f7a] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="settings-change-password-btn">
                    {passwordSaving ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                    {passwordSaving ? 'Modification...' : 'Changer le mot de passe'}
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}
